/**
 * GifPicker — Cerca e invia GIF da Tenor
 * Notion-style design, pannello sopra l'input
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, Loader2 } from 'lucide-react';

interface GifPickerProps {
  onSelect: (gifUrl: string) => void;
  onClose: () => void;
}

interface TenorGif {
  id: string;
  title: string;
  media_formats: {
    tinygif?: { url: string; dims: [number, number] };
    gif?: { url: string; dims: [number, number] };
    mediumgif?: { url: string; dims: [number, number] };
  };
}

// Tenor API key — può essere sovrascritta da env
const TENOR_API_KEY = import.meta.env.VITE_TENOR_API_KEY || 'AIzaSyAyimkuYQYF_FXVALexPuGQctUWRURdCYQ'; // Google default test key

export function GifPicker({ onSelect, onClose }: GifPickerProps) {
  const [query, setQuery] = useState('');
  const [gifs, setGifs] = useState<TenorGif[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchGifs = useCallback(async (searchQuery: string) => {
    setLoading(true);
    try {
      const endpoint = searchQuery.trim()
        ? `https://tenor.googleapis.com/v2/search?q=${encodeURIComponent(searchQuery)}&key=${TENOR_API_KEY}&limit=24&media_filter=tinygif,gif`
        : `https://tenor.googleapis.com/v2/featured?key=${TENOR_API_KEY}&limit=24&media_filter=tinygif,gif`;
      const res = await fetch(endpoint);
      const data = await res.json();
      setGifs(data.results || []);
    } catch {
      setGifs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load trending on mount
  useEffect(() => {
    fetchGifs('');
    inputRef.current?.focus();
  }, [fetchGifs]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchGifs(query), 400);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query, fetchGifs]);

  const getGifUrl = (gif: TenorGif) =>
    gif.media_formats.tinygif?.url || gif.media_formats.mediumgif?.url || gif.media_formats.gif?.url || '';

  return (
    <div
      className="absolute bottom-full mb-2 left-0 z-50 w-80 bg-white rounded-xl shadow-2xl overflow-hidden"
      style={{ border: '1px solid rgba(55,53,47,0.12)', maxHeight: 380 }}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: '1px solid rgba(55,53,47,0.06)' }}>
        <div className="relative flex-1 flex items-center">
          <Search className="absolute left-2 w-3.5 h-3.5 pointer-events-none" style={{ color: '#9B9A97' }} strokeWidth={1.5} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Cerca GIF..."
            className="w-full pl-7 pr-2 py-1.5 text-[13px] rounded-md outline-none"
            style={{ backgroundColor: 'rgba(55,53,47,0.06)', color: '#37352F', border: '1px solid transparent' }}
            onFocus={e => (e.target.style.borderColor = 'rgba(35,131,226,0.4)')}
            onBlur={e => (e.target.style.borderColor = 'transparent')}
          />
        </div>
        <button onClick={onClose} className="p-1 rounded hover:bg-[rgba(55,53,47,0.06)]">
          <X className="w-3.5 h-3.5" style={{ color: '#787774' }} strokeWidth={1.5} />
        </button>
      </div>

      {/* Grid */}
      <div className="overflow-y-auto p-1.5" style={{ maxHeight: 310 }}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin" style={{ color: '#2383E2' }} />
          </div>
        ) : gifs.length === 0 ? (
          <p className="text-center text-[12px] py-6" style={{ color: '#9B9A97' }}>
            {query ? 'Nessun risultato' : 'GIF non disponibili'}
          </p>
        ) : (
          <div className="grid grid-cols-3 gap-1">
            {gifs.map(gif => {
              const url = getGifUrl(gif);
              if (!url) return null;
              return (
                <button
                  key={gif.id}
                  onClick={() => onSelect(url)}
                  className="rounded-md overflow-hidden hover:opacity-80 transition-opacity"
                  style={{ aspectRatio: '1', backgroundColor: 'rgba(55,53,47,0.04)' }}
                >
                  <img
                    src={url}
                    alt={gif.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Tenor attribution */}
      <div className="px-3 py-1 text-center" style={{ borderTop: '1px solid rgba(55,53,47,0.06)' }}>
        <span className="text-[9px]" style={{ color: '#9B9A97' }}>Powered by Tenor</span>
      </div>
    </div>
  );
}
